import styled, { keyframes } from 'styled-components';
import Highcharts from 'highcharts/highcharts-gantt';
import HighchartsReact from 'highcharts-react-official';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACdetails, useCampaignContext } from 'context/CampaignContext';
import { useGetGanttOptions } from 'customHooks/useGanttOptions';
import { useLoggingActivity } from 'customHooks/useLogging';
import { IconButton } from 'components/style/Global';
import { useClientContext } from 'context/ClientContext';
// import CustomEvents from 'highcharts-custom-events';

// CustomEvents(Highcharts);

export const GanttContainerStyled = styled.div`
  width: calc(100vw - 4.5vw);
  padding: 10px;
  grid-column: span 3;
  background-color: ${({ theme }) => theme.placeholderBackground};
  border: 1px solid ${({ theme }) => theme.border};
  z-index: 0;
  /* overflow-x: scroll; */
  /* overflow-y: auto; */
  max-height: 800px;

  path {
    stroke: ${({ theme }) => theme.border};
  }
  .highcharts-xaxis-grid {
    path:not(:last-child) {
      stroke: none;
    }
  }
`;

type Props = {
  // campaigns: ACdetails[];
};

export const SkeletonPulse = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -200% 0; }
`;

export const Skeleton = styled.div`
  animation: ${SkeletonPulse} 1.5s ease-in-out infinite;
  background: linear-gradient(-90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  display: inline-block;
  line-height: 1;
  width: 100%;
`;

const GanttContainer = (props: Props) => {
  const { campaignList, fetchedPortalData } = useCampaignContext();
  const { anyPublish } = useClientContext();
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [series, setSeries] = useState<any[] | null>(null);
  const [o] = useGetGanttOptions(series);
  const [seter, setSeter] = useState<any | null>(null);
  const logActivity = useLoggingActivity();

  // Use ref to store the latest handleClick function
  const handleClickRef = useRef<((type: any, campaignId: number) => Promise<void>) | null>(null);

  const handleClick = useCallback(
    async (type: any, campaignId: number) => {
      const foundFlowchartCampaign = campaignList?.find((element: any) => element.campaignId === campaignId);
      if (foundFlowchartCampaign?.campaignId) {
        // Log the gantt chart campaign click activity
        const actionMap: { [key: string]: string } = {
          brief: 'BRIEF_TAB',
          plan: 'PLAN_TAB',
          summary: 'SUMMARY_TAB',
        };

        logActivity({
          action: actionMap[type] || 'GANTT_CLICK',
        }).catch(console.error);

        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('campaignID', foundFlowchartCampaign?.campaignId.toString());
          newParams.set('tab', type);
          return newParams;
        });
      }
    },
    [campaignList, logActivity, setSearchParams],
  );

  // Update the ref whenever handleClick changes
  handleClickRef.current = handleClick;
  useEffect(() => {
    campaignList && console.log('campaign list in gantt container', campaignList);
  }, [campaignList]);

  useEffect(() => {
    // Process immediately without timeout to avoid visual glitches
    // Early exit if no data
    if (!campaignList || campaignList.length === 0) {
      setSeter(null);
      return;
    }

    let seriesArray = [] as any[];
    const groupedPerClientCode = campaignList.reduce(
      (result: any, item: any) => ({
        ...result,
        [item['ClientCode']]: [...(result[item['ClientCode']] || []), item],
      }),
      {},
    );
    // PREP DATA FOR GANTT CHART
    if (groupedPerClientCode) {
      for (const [index, [key, value]] of Object.entries(groupedPerClientCode).entries()) {
        const ran = Math.random().toString(36).substring(7);
        // CREATE PARENT DATA AND ADD THEM TO A SERIES ARRAY
        const clientName = (value as ACdetails[]).find((v) => v?.ClientName)?.ClientName;
        seriesArray.push({
          name: clientName ? `${clientName} (${key})` : key,
          campaign_id: 123,
          id: key + ran,
          pointWidth: 3,
          collapsed: false, // Always expanded to prevent hiding issues
        });
        // CREATE CHILD DATA AND ADD THEM TO A SERIES ARRAY
        const childData = (value as []).map((element: ACdetails) => {
          if (element) {
            return {
              parent: key + ran,
              borderColor: 'transparent',
              name: element.campaign,
              start: new Date(element.startDate as string).getTime(),
              end: new Date(element.endDate as string).getTime(),
              campaign_id: element.campaignId,
              events: {
                click: function (event: any) {
                  // Handle the click event here
                  // handleClick(Date.now() > new Date(element.endDate as string).getTime() ? "summary" : Date.now() < new Date(element.startDate as string).getTime() ? "brief" : "plan", element.campaignId)
                  if (
                    Date.now() > new Date(element.endDate as string).getTime() ||
                    (Date.now() > new Date(element.startDate as string).getTime() &&
                      Date.now() < new Date(element.endDate as string).getTime())
                  ) {
                    handleClickRef.current?.('summary', element.campaignId);
                  } else {
                    if (element.status === 'CREATED' || element.status === 'NOT_BRIEFED') {
                      handleClickRef.current?.('brief', element.campaignId);
                    } else if (element.status === 'BRIEF_COMPLETED' || element.status === 'BRIEFED') {
                      handleClickRef.current?.('plan', element.campaignId);
                    } else if (
                      element.status === 'PLANNING' ||
                      element.status === 'PLANNING_DONE' ||
                      element.status === 'PLANNING_STARTED' ||
                      element.status === 'PLANNED'
                    ) {
                      if (element.authorized) {
                        handleClickRef.current?.('summary', element.campaignId);
                      } else if (!element.authorized) {
                        handleClickRef.current?.('plan', element.campaignId);
                      }
                    } else if (
                      element.status === 'CAMPAIGN_PUBLISHED' ||
                      element.status === 'CAMPAIGN_PARTLY_PUBLISHED'
                    ) {
                      handleClickRef.current?.('summary', element.campaignId);
                    }
                  }
                },
              },
              dataLabels: {
                enabled: true,
                useHTML: true,
                format:
                  element.authorized &&
                  element.status === 'CAMPAIGN_PARTLY_PUBLISHED' &&
                  Date.now() > new Date(element.endDate as string).getTime()
                    ? `<div style="display: flex; flex-direction: row; justify-content: flex-end; align-items: center; pointer-events: none;"><svg width="15" height="15"><circle cx="7" cy="7" r="5"  stroke-width="1" fill="#ffad9b" /></svg></div>`
                    : element.authorized &&
                        element.status ===
                          'PLANNING_STARTED' /* && Date.now() > new Date(element.endDate as string).getTime() */
                      ? `<div style="display: flex; flex-direction: row; justify-content: flex-end; align-items: center; pointer-events: none;"><svg width="15" height="15"><circle cx="7" cy="7" r="5"  stroke-width="1" fill="#E2FFE8" /></svg></div>`
                      : '',
                style: {
                  color: '#000000',
                  fontWeight: 'normal',
                  textOutline: 'none',
                },

                // x: Number(scaledWidth),
              },
              //  color: Date.now() > new Date(element.endDate as string).getTime() ? "green" : Date.now() < new Date(element.startDate as string).getTime() ? "pink" : "yellow"
              color:
                (element.status === 'NOT_BRIEFED' || element.status === 'CREATED') &&
                Date.now() < new Date(element.startDate as string).getTime() //IF THE CAMPAIGN IS NOT BRIEFED AND THE START DATE IS IN THE FUTURE
                  ? '#BFBFBF'
                  : (element.status === 'BRIEFED' ||
                        element.status === 'BRIEF_COMPLETED' ||
                        element.status === 'BRIEFED_FROM_CLIENT' ||
                        element.status === 'PLANNING_STARTED') &&
                      Date.now() < new Date(element.startDate as string).getTime() //IF THE CAMPAIGN IS BRIEFED AND THE START DATE IS IN THE FUTURE
                    ? '#EBC57D'
                    : (element.status === 'UNPUBLISHED' ||
                          element.status === 'PLANNING_DONE' ||
                          element.status === 'PLANNED') &&
                        Date.now() < new Date(element.startDate as string).getTime() //IF THE CAMPAIGN IS PLANNED AND THE START DATE IS IN THE FUTURE
                      ? '#E2FFE8'
                      : Date.now() > new Date(element.startDate as string).getTime() &&
                          Date.now() < new Date(element.endDate as string).getTime() //IF THE CAMPAIGN IS ACTIVE
                        ? '#A9C8C0'
                        : Date.now() > new Date(element.endDate as string).getTime() &&
                            element.status !== 'CAMPAIGN_PARTLY_PUBLISHED' &&
                            element.status !== 'CAMPAIGN_PUBLISHED' //IF THE CAMPAIGN IS COMPLETED
                          ? '#7D98AB'
                          : Date.now() > new Date(element.endDate as string).getTime() &&
                              element.status === 'CAMPAIGN_PARTLY_PUBLISHED' //IF THE CAMPAIGN IS PARTLY PUBLISHED
                            ? /* '#8BAEDF' */ '#7D98AB'
                            : Date.now() > new Date(element.endDate as string).getTime() &&
                                element.status === 'CAMPAIGN_PUBLISHED' //IF THE CAMPAIGN IS PUBLISHED
                              ? '#AFA2C3'
                              : '#000000',
              // color: '#BFBFBF',
            };
          }
          return null; // Return null if element is undefined
        });

        seriesArray.push(...childData);
      }
      setSeter(seriesArray);
    } else {
      setSeter(null);
    }
  }, [campaignList]);

  useEffect(() => {
    if (seter !== null) {
      setSeries(seter);
    } else {
      setSeries([]);
    }
  }, [seter]);

  const handleCampaignSummaryDownload = () => {
    if (!fetchedPortalData || fetchedPortalData.length === 0 || !campaignList) return;

    // Filter fetchedPortalData based on campaignList
    const campaignIds = campaignList.map((campaign) => campaign?.campaignId);
    const filteredData = fetchedPortalData.filter(
      (data: any) =>
        campaignIds.includes(data.campaign_id) ||
        campaignList.some((campaign) => campaign?.campaign === data.campaignName),
    );

    // Enhance filtered data with campaignName from campaignList
    const enhancedData = filteredData.map((data: any) => {
      const matchingCampaign = campaignList.find(
        (campaign) => campaign?.campaignId === data.campaign_id || campaign?.campaign === data.campaignName,
      );

      return {
        'campaign name': matchingCampaign?.campaign || data.campaignName || 'N/A',
        ...data,
      };
    });

    if (enhancedData.length === 0) return;

    // Get headers from the first object
    const headers = Object.keys(enhancedData[0]);

    // Create CSV content
    const csvContent = [
      // Headers row
      headers.join(','),
      // Data rows
      ...enhancedData.map((row) =>
        headers
          .map((header) => {
            const value = (row as any)[header];
            // Handle values that might contain commas, quotes, or newlines
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(','),
      ),
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaignSummary_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  return (
    <GanttContainerStyled ref={chartContainerRef}>
      {o !== null && campaignList !== undefined ? (
        <div style={{ paddingTop: '50px' }}>
          {anyPublish &&
            fetchedPortalData &&
            fetchedPortalData.length > 0 &&
            fetchedPortalData[0]?.campaign_id !== -1 && (
              <IconButton
                title="Download these campaigns as CSV"
                onClick={handleCampaignSummaryDownload}
                style={{
                  position: 'absolute',
                  top: 70,
                  left: 30,
                  zIndex: 1,
                  padding: 5,
                }}
              >
                <span className="svg material-symbols-outlined ">download</span>
              </IconButton>
            )}
          <HighchartsReact
            key={`gantt-${campaignList?.length || 0}-${campaignList?.[0]?.campaignId || 'none'}`}
            highcharts={Highcharts}
            constructorType={'ganttChart'}
            options={o}
            allowChartUpdate={true}
            styledMode={true}
          />
        </div>
      ) : (
        <div>
          {Array(20)
            .fill(0)
            .map((_, index) => (
              <Skeleton key={index} style={{ height: '35px', marginTop: index > 0 ? '10px' : '0' }} />
            ))}
        </div>
      )}
    </GanttContainerStyled>
  );
};

export default GanttContainer;
